// The one place every draggable line handle is created, so they all look
// identical — a square (line endpoints, middle segment, curve points) or a
// circle (line-docking handles) of `LINE_HANDLE_SIZE_PX`, in the same white and
// grey-blue as the transformer's resize anchors. New interactive handles should
// build their node here instead of hand-rolling a shape.
//
// The grab area is exactly the visible handle (Anke, 2026-09-29): no
// `hitStrokeWidth` reserve, so Konva hit-tests the fill plus its 1 px outline.

import Konva from 'konva';
import {
  LINE_HANDLE_SIZE_PX,
  HANDLE_FILL,
  HANDLE_STROKE,
} from '../../constants/whiteboard-config.constants.js';

export interface SelectionHandleOptions {
  /** Centre of the handle, in the layer's coordinate frame. */
  readonly x: number;
  readonly y: number;
  /** Viewport scale, so the handle stays screen-constant. */
  readonly scale: number;
  /** Konva node name (used to find/tag the handle). */
  readonly name: string;
  /** Defaults to true. */
  readonly draggable?: boolean;
}

/**
 * Create the standard handle: a square centred on `(x,y)`, screen-constant via
 * `scale`. Centring is done
 * with an offset so `node.position()` is the centre — callers can read/write
 * the centre directly, exactly like a circle handle.
 */
export function createSelectionHandle(opts: SelectionHandleOptions): Konva.Rect {
  const size = LINE_HANDLE_SIZE_PX / opts.scale;
  return new Konva.Rect({
    x: opts.x,
    y: opts.y,
    width: size,
    height: size,
    offsetX: size / 2,
    offsetY: size / 2,
    fill: HANDLE_FILL,
    stroke: HANDLE_STROKE,
    strokeWidth: 1,
    strokeScaleEnabled: false,
    draggable: opts.draggable ?? true,
    name: opts.name,
  });
}

/** Keep a square handle screen-constant on zoom, centred on its position. */
export function sizeSelectionHandle(handle: Konva.Rect, scale: number): void {
  const size = LINE_HANDLE_SIZE_PX / scale;
  handle.size({ width: size, height: size });
  handle.offsetX(size / 2);
  handle.offsetY(size / 2);
}

/**
 * Round variant of {@link createSelectionHandle}: a circle with the same
 * standard look. Used where a handle needs to read as distinct
 * from the square resize anchors (e.g. the edge connector handles). Diameter
 * equals the line handle square's side, so both kinds look equally big.
 */
export function createRoundSelectionHandle(opts: SelectionHandleOptions): Konva.Circle {
  return new Konva.Circle({
    x: opts.x,
    y: opts.y,
    radius: LINE_HANDLE_SIZE_PX / 2 / opts.scale,
    fill: HANDLE_FILL,
    stroke: HANDLE_STROKE,
    strokeWidth: 1,
    strokeScaleEnabled: false,
    draggable: opts.draggable ?? true,
    name: opts.name,
  });
}

/** Keep a round handle's radius screen-constant on zoom. */
export function sizeRoundSelectionHandle(handle: Konva.Circle, scale: number): void {
  handle.radius(LINE_HANDLE_SIZE_PX / 2 / scale);
}
